import { ERROR_CODE } from '@config/errors';

import {
  defineAuthErrorCode,
  isBaseNetErrorAuthBody,
  isBaseNetErrorBody,
  isBaseNetErrorBodyFieldError,
} from '@helpers/errors';
import { lowercaseFirstLetter } from '@helpers/format';

import { BaseNetErrorBodyData } from '@services/dto/errors/base-net-error';

type ApiErrorDto = {
  Code?: number | string;
  code?: number | string;
  Message?: string;
  message?: string;
};

const getApiErrorCode = (value: ApiErrorDto): number | undefined => {
  const code = value.Code ?? value.code;
  const normalizedCode = Number(code);

  return Number.isFinite(normalizedCode) ? normalizedCode : undefined;
};

const getApiErrors = (dto: unknown): number[] | undefined => {
  const body = typeof dto === 'object' && dto !== null ? dto : undefined;
  const possibleErrors = Array.isArray(dto)
    ? dto
    : (body as { Errors?: unknown; errors?: unknown } | undefined)?.Errors ??
      (body as { errors?: unknown } | undefined)?.errors ??
      (body ? [body] : undefined);

  if (!Array.isArray(possibleErrors)) {
    return undefined;
  }

  const codes = possibleErrors.map((error) =>
    typeof error === 'object' && error !== null
      ? getApiErrorCode(error as ApiErrorDto)
      : undefined
  );

  return codes.every((code): code is number => code !== undefined)
    ? codes
    : undefined;
};

export const baseNetErrorBodyFromDto = (dto: unknown): BaseNetErrorBodyData => {
  const apiErrors = getApiErrors(dto);

  if (apiErrors) {
    return {
      data: null,
      errors: apiErrors.map((code) => ({
        code: code as ERROR_CODE,
        data: undefined,
      })),
    };
  }

  /**
   * isNetErrorBody condition check must be placed as first because server
   * is able to return structure:
   * {
   *   error: "invalid_grant",
   *   error_description: "Account is deleted",
   *   message: "Account is deleted",
   *   errors: [
   *     {
   *       code: 20140
   *     }
   *   ],
   *   data: null
   * }
   */
  if (isBaseNetErrorBody(dto)) {
    return {
      data: dto.data,
      errors: dto.errors.map((error) => {
        if (isBaseNetErrorBodyFieldError(error)) {
          return {
            ...error,
            data: {
              ...error.data,
              field: lowercaseFirstLetter(error.data.field),
            },
          };
        }

        return error;
      }),
    };
  }

  if (isBaseNetErrorAuthBody(dto)) {
    return {
      data: null,
      errors: [
        {
          code: defineAuthErrorCode(dto.message),
          data: undefined,
        },
      ],
    };
  }

  return {
    data: null,
    errors: [
      {
        code: ERROR_CODE.UNEXPECTED_ERROR,
        data: undefined,
      },
    ],
  };
};
